import React, { useState, useEffect, useMemo, useRef } from 'react';
import { PackageOpen, Search, RefreshCw, Calendar, User, AlertCircle, Package, ChevronDown, ChevronRight, Loader, Check, X, Edit3, Truck } from 'lucide-react';
import { pOrdersService } from '../../../../services/pOrdersService';
import { pOrderLineService } from '../../../../services/pOrderLineService';
import { POrdersDto, POrderLineDto } from '../../../../types/porders';
import { BackButton } from '../../../common/BackButton';

interface EditableHeaderData {
  orderNoERP: string;
  supplierName: string;
  deliveryDate: string;
  yourRef: string;
  ourRef: string;
  warehouse: string;
}

interface HeaderEditState {
  [key: string]: EditableHeaderData;
}

interface HeaderSaveState {
  [key: string]: 'saved' | 'saving' | 'error';
}

const EDITABLE_FIELDS = ['orderNoERP', 'supplierName', 'deliveryDate', 'yourRef', 'ourRef', 'warehouse'] as const;
type EditableField = typeof EDITABLE_FIELDS[number];

interface WorkingOrdersProps {
  onBack?: () => void;
}

const WorkingOrders: React.FC<WorkingOrdersProps> = ({ onBack }) => {
  const [orders, setOrders] = useState<POrdersDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedOrders, setExpandedOrders] = useState<Set<number>>(new Set());
  const [orderLines, setOrderLines] = useState<Map<number, POrderLineDto[]>>(new Map());
  const [loadingLines, setLoadingLines] = useState<Set<number>>(new Set());
  
  // Editing state
  const [focusedCell, setFocusedCell] = useState<string | null>(null);
  const [editState, setEditState] = useState<HeaderEditState>({});
  const [changedHeaders, setChangedHeaders] = useState<Set<string>>(new Set());
  const [saveState, setSaveState] = useState<HeaderSaveState>({});
  
  const inputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await pOrdersService.getOrdersForReceiving();
      setOrders(data);
      
      // Initialize edit state for all headers
      const newEditState: HeaderEditState = {};
      data.forEach(order => {
        const key = `${order.orderNo}`;
        newEditState[key] = {
          orderNoERP: order.orderNoERP || '',
          supplierName: order.supplierName || '',
          deliveryDate: order.deliveryDate ? new Date(order.deliveryDate).toISOString().split('T')[0] : '',
          yourRef: order.yourRef || '',
          ourRef: order.ourRef || '',
          warehouse: order.warehouse || ''
        };
      });
      setEditState(newEditState);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Feil ved lasting av bestillinger');
      console.error('Error loading orders:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleOrderExpansion = async (orderNo: number) => {
    const newExpanded = new Set(expandedOrders);
    
    if (newExpanded.has(orderNo)) {
      newExpanded.delete(orderNo);
      setExpandedOrders(newExpanded);
    } else {
      newExpanded.add(orderNo);
      setExpandedOrders(newExpanded);
      
      if (!orderLines.has(orderNo)) {
        await loadOrderLines(orderNo);
      }
    }
  };

  const loadOrderLines = async (orderNo: number) => {
    try {
      setLoadingLines(prev => new Set(prev).add(orderNo));
      const lines = await pOrderLineService.getOrderLines(orderNo);
      setOrderLines(prev => new Map(prev).set(orderNo, lines));
    } catch (err) {
      console.error(`Error loading lines for order ${orderNo}:`, err);
      alert(`Feil ved lasting av linjer: ${err instanceof Error ? err.message : 'Ukjent feil'}`);
    } finally {
      setLoadingLines(prev => {
        const newSet = new Set(prev);
        newSet.delete(orderNo);
        return newSet;
      });
    }
  };

  const getHeaderKey = (orderNo: number) => `${orderNo}`;
  const getCellKey = (orderNo: number, field: string) => `${orderNo}-${field}`;

  const handleCellFocus = (orderNo: number, field: string) => {
    const cellKey = getCellKey(orderNo, field);
    setFocusedCell(cellKey);
    
    setTimeout(() => {
      const input = inputRefs.current[cellKey];
      if (input) {
        input.focus();
        input.select();
      }
    }, 0);
  };

  const handleCellChange = (orderNo: number, field: keyof EditableHeaderData, value: any) => {
    const headerKey = getHeaderKey(orderNo);
    
    setEditState(prev => ({
      ...prev,
      [headerKey]: {
        ...prev[headerKey],
        [field]: value
      }
    }));
    
    setChangedHeaders(prev => new Set(prev).add(headerKey));
  };

  const saveHeaderChanges = async (orderNo: number) => {
    const headerKey = getHeaderKey(orderNo);
    
    if (!changedHeaders.has(headerKey)) {
      return;
    }

    const editData = editState[headerKey];
    if (!editData) return;

    const originalOrder = orders.find(o => o.orderNo === orderNo);
    if (!originalOrder) return;

    try {
      setSaveState(prev => ({ ...prev, [headerKey]: 'saving' }));

      const orderUpdate: Partial<POrdersDto> = {
        ...originalOrder,
        orderNoERP: editData.orderNoERP,
        supplierName: editData.supplierName,
        deliveryDate: editData.deliveryDate ? new Date(editData.deliveryDate) : undefined,
        yourRef: editData.yourRef,
        ourRef: editData.ourRef,
        warehouse: editData.warehouse
      };

      await pOrdersService.updateOrder(orderNo, orderUpdate);
      
      setOrders(prev => prev.map(o => 
        o.orderNo === orderNo 
          ? { ...o, ...orderUpdate }
          : o
      ));
      
      setChangedHeaders(prev => {
        const newSet = new Set(prev);
        newSet.delete(headerKey);
        return newSet;
      });
      
      setSaveState(prev => ({ ...prev, [headerKey]: 'saved' }));
      setTimeout(() => {
        setSaveState(prev => {
          const newState = { ...prev };
          delete newState[headerKey];
          return newState;
        });
      }, 2000);
      
    } catch (err) {
      console.error('Error saving header:', err);
      setSaveState(prev => ({ ...prev, [headerKey]: 'error' }));
      alert(`Feil ved lagring: ${err instanceof Error ? err.message : 'Ukjent feil'}`);
      
      setTimeout(() => {
        setSaveState(prev => {
          const newState = { ...prev };
          delete newState[headerKey];
          return newState;
        });
      }, 3000);
    }
  };

  const navigateToCell = (orderNo: number, field: string) => {
    const cellKey = getCellKey(orderNo, field);
    setFocusedCell(cellKey);
    setTimeout(() => {
      const input = inputRefs.current[cellKey];
      if (input) {
        input.focus();
        input.select();
      }
    }, 0);
  };

  const handleKeyDown = async (
    e: React.KeyboardEvent<HTMLInputElement>,
    orderNo: number,
    field: string,
    orderIndex: number,
    fieldIndex: number
  ) => {
    const totalOrders = filteredOrders.length;

    if (e.key === 'Tab') {
      e.preventDefault();
      const newFieldIndex = e.shiftKey ? fieldIndex - 1 : fieldIndex + 1;
      
      if (newFieldIndex >= 0 && newFieldIndex < EDITABLE_FIELDS.length) {
        const newField = EDITABLE_FIELDS[newFieldIndex];
        navigateToCell(orderNo, newField);
      } else if (!e.shiftKey && newFieldIndex >= EDITABLE_FIELDS.length) {
        if (orderIndex + 1 < totalOrders) {
          const newOrder = filteredOrders[orderIndex + 1];
          navigateToCell(newOrder.orderNo, EDITABLE_FIELDS[0]);
        }
      } else if (e.shiftKey && newFieldIndex < 0) {
        if (orderIndex > 0) {
          const newOrder = filteredOrders[orderIndex - 1];
          navigateToCell(newOrder.orderNo, EDITABLE_FIELDS[EDITABLE_FIELDS.length - 1]);
        }
      }
      return;
    }

    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
      
      await saveHeaderChanges(orderNo);
      
      const newOrderIndex = e.key === 'ArrowDown' ? orderIndex + 1 : orderIndex - 1;
      if (newOrderIndex >= 0 && newOrderIndex < totalOrders) {
        const newOrder = filteredOrders[newOrderIndex];
        navigateToCell(newOrder.orderNo, field);
      }
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      e.preventDefault();
      
      const newFieldIndex = e.key === 'ArrowRight' ? fieldIndex + 1 : fieldIndex - 1;
      if (newFieldIndex >= 0 && newFieldIndex < EDITABLE_FIELDS.length) {
        const newField = EDITABLE_FIELDS[newFieldIndex];
        navigateToCell(orderNo, newField);
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      await saveHeaderChanges(orderNo);
      
      if (orderIndex + 1 < totalOrders) {
        const newOrder = filteredOrders[orderIndex + 1];
        navigateToCell(newOrder.orderNo, field);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      const headerKey = getHeaderKey(orderNo);
      const originalOrder = orders.find(o => o.orderNo === orderNo);
      if (originalOrder) {
        setEditState(prev => ({
          ...prev,
          [headerKey]: {
            orderNoERP: originalOrder.orderNoERP || '',
            supplierName: originalOrder.supplierName || '',
            deliveryDate: originalOrder.deliveryDate ? new Date(originalOrder.deliveryDate).toISOString().split('T')[0] : '',
            yourRef: originalOrder.yourRef || '',
            ourRef: originalOrder.ourRef || '',
            warehouse: originalOrder.warehouse || ''
          }
        }));
        setChangedHeaders(prev => {
          const newSet = new Set(prev);
          newSet.delete(headerKey);
          return newSet;
        });
      }
      setFocusedCell(null);
    }
  };

  const handleCellBlur = async (orderNo: number, e: React.FocusEvent<HTMLInputElement>) => {
    await saveHeaderChanges(orderNo);
    
    const newFocusTarget = e.relatedTarget as HTMLElement;
    if (!newFocusTarget || newFocusTarget.tagName !== 'INPUT') {
      setFocusedCell(null);
    }
  };

  const orderMatchesSearch = (order: POrdersDto): boolean => {
    if (!searchTerm) return true;

    const searchLower = searchTerm.toLowerCase();
    return (
      order.orderNo.toString().includes(searchLower) ||
      (order.orderNoERP || '').toLowerCase().includes(searchLower) ||
      (order.supplierName || '').toLowerCase().includes(searchLower) ||
      (order.yourRef || '').toLowerCase().includes(searchLower) ||
      (order.ourRef || '').toLowerCase().includes(searchLower)
    );
  };

  const filteredOrders = useMemo(() => {
    return orders.filter(orderMatchesSearch);
  }, [orders, searchTerm]);

  const getStatusColor = (status: number): string => {
    switch (status) {
      case 0: return 'bg-gray-100 text-gray-800';
      case 10: return 'bg-green-100 text-green-800';
      case 20: return 'bg-blue-100 text-blue-800';
      case 30: return 'bg-yellow-100 text-yellow-800';
      case 40: return 'bg-green-100 text-green-800';
      case 45: return 'bg-purple-100 text-purple-800';
      case 50: return 'bg-indigo-100 text-indigo-800';
      case 60: return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getRowClassName = (orderNo: number): string => {
    const headerKey = getHeaderKey(orderNo);
    const state = saveState[headerKey];
    const isChanged = changedHeaders.has(headerKey);
    
    if (state === 'saved') return 'bg-green-50 hover:bg-green-100 transition-colors duration-200';
    if (state === 'error') return 'bg-red-50 hover:bg-red-100';
    if (isChanged) return 'bg-yellow-50 hover:bg-yellow-100';
    return 'hover:bg-gray-50';
  };

  const renderEditableCell = (
    orderNo: number,
    field: EditableField,
    fieldIndex: number,
    orderIndex: number,
    type: 'text' | 'date' = 'text'
  ) => {
    const headerKey = getHeaderKey(orderNo);
    const cellKey = getCellKey(orderNo, field);
    const isFocused = focusedCell === cellKey;
    const value = editState[headerKey]?.[field] ?? '';

    return (
      <div className="relative">
        <input
          ref={el => inputRefs.current[cellKey] = el}
          type={type}
          value={value}
          onChange={(e) => handleCellChange(orderNo, field, e.target.value)}
          onFocus={() => handleCellFocus(orderNo, field)}
          onKeyDown={(e) => handleKeyDown(e, orderNo, field, orderIndex, fieldIndex)}
          onBlur={(e) => handleCellBlur(orderNo, e)}
          className={`
            w-full px-2 py-1 text-sm
            border rounded
            transition-all duration-150
            ${isFocused 
              ? 'border-blue-500 ring-2 ring-blue-200 bg-white' 
              : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50 bg-white'
            }
            focus:outline-none
            cursor-pointer
          `}
          style={{ minWidth: type === 'date' ? '120px' : '120px' }}
        />
        {!isFocused && (
          <div className="absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none">
            <Edit3 size={10} className="text-gray-400" />
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center space-x-2">
          <RefreshCw className="animate-spin" size={20} />
          <span>Laster bestillinger...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <h3 className="text-red-800 font-medium">Feil ved lasting av bestillinger</h3>
        <p className="text-red-600 text-sm mt-1">{error}</p>
        <button
          onClick={loadOrders}
          className="mt-3 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 flex items-center space-x-2"
        >
          <RefreshCw size={16} />
          <span>Prøv igjen</span>
        </button>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="mb-6 px-6 pt-6">
        <div className="flex items-center space-x-4 mb-4">
          <BackButton onClick={onBack || (() => {})} />
          <h1 className="text-2xl font-bold text-gray-800 flex items-center space-x-2">
            <PackageOpen size={24} />
            <span>Administrer bestillinger</span>
          </h1>
        </div>
        <p className="text-gray-600 mt-1">
          Oversikt over bestillinger for varemottak - {filteredOrders.length} av {orders.length} bestillinger vises
          {changedHeaders.size > 0 && (
            <span className="ml-2 px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium">
              {changedHeaders.size} endring{changedHeaders.size > 1 ? 'er' : ''} ikke lagret
            </span>
          )}
        </p>
        <p className="text-gray-500 text-sm mt-1">
          💡 Tips: Klikk i en celle med blå bakgrunn for å redigere. Bruk piltaster, Tab eller Enter for å navigere. Escape for å avbryte.
        </p>
      </div>

      {/* Search and Controls */}
      <div className="bg-white rounded-lg shadow-sm border mb-4 p-4 mx-6">
        <div className="flex flex-col lg:flex-row lg:items-center space-y-3 lg:space-y-0 lg:space-x-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Søk etter ordrenummer, leverandør, referanse..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={loadOrders}
              disabled={loading}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2"
            >
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
              <span>Oppdater</span>
            </button>
          </div>
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden mx-6 mb-6">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-8">
                  
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                  Status
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ordre Nr
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-blue-50">
                  <Edit3 size={10} className="inline mr-1" />
                  ERP Ordrenr
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-blue-50">
                  <Edit3 size={10} className="inline mr-1" />
                  Leverandør
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ordredato
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-blue-50">
                  <Edit3 size={10} className="inline mr-1" />
                  Leveringsdato
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-blue-50">
                  <Edit3 size={10} className="inline mr-1" />
                  Vår ref
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-blue-50">
                  <Edit3 size={10} className="inline mr-1" />
                  Deres ref
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-blue-50">
                  <Edit3 size={10} className="inline mr-1" />
                  Lager
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ordrestatus
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredOrders.map((order, orderIndex) => {
                const headerKey = getHeaderKey(order.orderNo);
                const state = saveState[headerKey];
                const isExpanded = expandedOrders.has(order.orderNo);
                const lines = orderLines.get(order.orderNo);
                const isLoadingLines = loadingLines.has(order.orderNo);
                
                return (
                  <React.Fragment key={order.id}>
                    <tr className={getRowClassName(order.orderNo)}>
                      <td 
                        className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 cursor-pointer"
                        onClick={() => toggleOrderExpansion(order.orderNo)}
                      >
                        <div className="flex items-center">
                          {isExpanded ? (
                            <ChevronDown size={14} className="text-gray-400" />
                          ) : (
                            <ChevronRight size={14} className="text-gray-400" />
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center justify-center">
                          {state === 'saving' && <Loader className="animate-spin text-blue-600" size={14} />}
                          {state === 'saved' && <Check className="text-green-600 animate-pulse" size={14} />}
                          {state === 'error' && <X className="text-red-600" size={14} />}
                          {!state && changedHeaders.has(headerKey) && (
                            <span className="flex items-center justify-center w-4 h-4 bg-yellow-400 rounded-full text-white text-xs font-bold">
                              !
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="text-sm font-medium text-gray-900">{order.orderNo}</div>
                      </td>
                      <td className="px-3 py-2 bg-blue-50">{renderEditableCell(order.orderNo, 'orderNoERP', 0, orderIndex, 'text')}</td>
                      <td className="px-3 py-2 bg-blue-50">{renderEditableCell(order.orderNo, 'supplierName', 1, orderIndex, 'text')}</td>
                      <td className="px-3 py-2">
                        <div className="text-sm text-gray-900 flex items-center space-x-1">
                          <Calendar size={12} className="text-gray-400" />
                          <span>{pOrdersService.formatDate(order.orderDate)}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2 bg-blue-50">{renderEditableCell(order.orderNo, 'deliveryDate', 2, orderIndex, 'date')}</td>
                      <td className="px-3 py-2 bg-blue-50">{renderEditableCell(order.orderNo, 'yourRef', 3, orderIndex, 'text')}</td>
                      <td className="px-3 py-2 bg-blue-50">{renderEditableCell(order.orderNo, 'ourRef', 4, orderIndex, 'text')}</td>
                      <td className="px-3 py-2 bg-blue-50">{renderEditableCell(order.orderNo, 'warehouse', 5, orderIndex, 'text')}</td>
                      <td className="px-3 py-2">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(order.orderStatus)}`}>
                          {order.statusText}
                        </span>
                      </td>
                    </tr>

                    {/* Expanded Lines Section */}
                    {isExpanded && (
                      <tr>
                        <td colSpan={11} className="px-3 py-3 bg-gray-50">
                          {isLoadingLines ? (
                            <div className="flex items-center justify-center py-8">
                              <Loader className="animate-spin mr-2" size={20} />
                              <span className="text-gray-600">Laster linjer...</span>
                            </div>
                          ) : lines ? (
                            <div className="overflow-x-auto">
                              <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center space-x-2">
                                <Package size={16} />
                                <span>Ordrelinjer for ordre {order.orderNo}</span>
                                <span className="text-xs text-gray-500">
                                  ({lines.length} linjer)
                                </span>
                              </h4>
                              
                              {lines.length > 0 ? (
                                <table className="min-w-full text-sm border border-gray-200 rounded">
                                  <thead className="bg-gray-100">
                                    <tr className="border-b border-gray-300">
                                      <th className="text-left py-2 px-3 text-xs font-medium text-gray-600 uppercase">Linjenr</th>
                                      <th className="text-left py-2 px-3 text-xs font-medium text-gray-600 uppercase">Varenummer</th>
                                      <th className="text-left py-2 px-3 text-xs font-medium text-gray-600 uppercase">Beskrivelse</th>
                                      <th className="text-left py-2 px-3 text-xs font-medium text-gray-600 uppercase">Antall</th>
                                      <th className="text-left py-2 px-3 text-xs font-medium text-gray-600 uppercase">Levert</th>
                                      <th className="text-left py-2 px-3 text-xs font-medium text-gray-600 uppercase">Gjenstående</th>
                                      <th className="text-left py-2 px-3 text-xs font-medium text-gray-600 uppercase">Enhet</th>
                                      <th className="text-left py-2 px-3 text-xs font-medium text-gray-600 uppercase">Lager</th>
                                      <th className="text-left py-2 px-3 text-xs font-medium text-gray-600 uppercase">Status</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-gray-200 bg-white">
                                    {lines.map((line) => (
                                      <tr key={line.id} className="hover:bg-gray-50">
                                        <td className="py-2 px-3 text-gray-900 font-medium">{line.lineNo}</td>
                                        <td className="py-2 px-3 text-gray-900 font-mono text-xs">{line.itemNo || '-'}</td>
                                        <td className="py-2 px-3 text-gray-900 text-xs">{line.description1 || '-'}</td>
                                        <td className="py-2 px-3 text-gray-900">{line.quantity?.toFixed(2) || '0.00'}</td>
                                        <td className="py-2 px-3 text-gray-900">{line.quantityDelivered?.toFixed(2) || '0.00'}</td>
                                        <td className="py-2 px-3 text-gray-900 font-medium">{line.remainingQuantity?.toFixed(2) || '0.00'}</td>
                                        <td className="py-2 px-3 text-gray-900 text-xs">{line.itemUnitCode || '-'}</td>
                                        <td className="py-2 px-3 text-gray-900 text-xs">{line.warehouse || '-'}</td>
                                        <td className="py-2 px-3 text-gray-900 text-xs">{line.statusText || '-'}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              ) : (
                                <div className="text-center py-8 text-gray-500">
                                  Ingen linjer funnet for denne bestillingen
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="text-center py-8 text-gray-500">
                              Kunne ikke laste linjer
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {filteredOrders.length === 0 && (
          <div className="text-center py-12">
            <PackageOpen size={48} className="mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-1">Ingen bestillinger funnet</h3>
            <p className="text-gray-500">
              {orders.length === 0 
                ? 'Ingen bestillinger for varemottak funnet.' 
                : 'Ingen bestillinger matcher søkekriteriene.'
              }
            </p>
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="mt-3 text-blue-600 hover:text-blue-800 text-sm font-medium"
              >
                Fjern filter
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkingOrders;
